import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, Settings, Gift, Wallet, Trophy, Compass, LayoutGrid, Shield, LogOut,
  Briefcase, FileSignature, Mic, AudioLines,
} from "lucide-react";
import { toast } from "sonner";
import HubPage, { type HubGroup } from "@/components/shell/HubPage";
import AssessmentHistory from "@/components/home/AssessmentHistory";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { fetchAttempts, type Attempt } from "@/lib/assessmentAttempts";

export default function YouHub() {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Attempt[]>([]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    fetchAttempts(user.id)
      .then((r) => active && setRows(r))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [user]);

  const groups: HubGroup[] = [
    {
      title: "Account",
      links: [
        { to: "/profile", label: "My profile", hint: "Your details and photo", icon: User },
        { to: "/settings", label: "Settings", hint: "Preferences, privacy and reminders", icon: Settings },
        { to: "/dashboard", label: "History", hint: "Every tool, past attempts, rewards", icon: LayoutGrid },
        { to: "/start", label: "Retake the assessment", hint: "See how you've grown", icon: Compass },
      ],
    },
    {
      title: "Career tools",
      links: [
        { to: "/opportunities", label: "Opportunities", hint: "Internships and openings", icon: Briefcase },
        { to: "/resume", label: "Resume", hint: "Build and refine yours", icon: FileSignature },
        { to: "/interview", label: "Mock Interview", hint: "Practise out loud", icon: Mic },
        { to: "/journal", label: "Voice Journal", hint: "Think it through, spoken", icon: AudioLines },
      ],
    },
    {
      title: "Community",
      links: [
        { to: "/refer", label: "Invite friends", hint: "Share and earn rewards", icon: Gift },
        { to: "/affiliate", label: "Creator programme", hint: "For creators and partners", icon: Wallet },
        { to: "/certifications", label: "Certifications", hint: "NSDC-approved, matched to your careers", icon: Trophy },
        { to: "/rewards", label: "Rewards & badges", hint: "Daily challenges, streaks, badges", icon: Trophy },
        { to: "/leaderboard", label: "Leaderboards", hint: "Community progress", icon: Trophy },
      ],
    },
  ];
  if (isAdmin) {
    groups.push({
      title: "Admin",
      links: [{ to: "/admin", label: "Admin", hint: "Manage the platform", icon: Shield }],
    });
  }

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  return (
    <HubPage title="You" intro="Your account, your history and your tools." path="/hub/you" groups={groups}>
      <div id="history" className="scroll-mt-24">
        <AssessmentHistory rows={rows} />
      </div>
      <Button variant="secondary" className="h-12 w-full rounded-2xl" onClick={handleSignOut}>
        <LogOut className="mr-2 h-4 w-4" aria-hidden="true" /> Sign out
      </Button>
    </HubPage>
  );
}
